/**
 * Canonical source-of-record rules for manifest data.
 *
 * Three pillars contribute to a manifest:
 *   1. Document (extracted from the uploaded/signed PDF — the ultimate
 *      source of authority, but only once a human has reviewed the data)
 *   2. e-Manifest (EPA)
 *   3. Vendor (CSV import from a hauler)
 *
 * Display preference is `document -> eManifest -> vendor`: the physical
 * manifest document is what everyone actually signed, so its reviewed data
 * outranks the EPA feed. An UNREVIEWED document is not a source at all —
 * callers must gate `hasDocument` on {@link isManifestDocumentReviewed}
 * before passing it here. Tie-breaker: if both e-Manifest and vendor exist
 * AND a staff user manually edited the vendor data, vendor wins over
 * e-Manifest (preserves intentional staff overrides; a reviewed document
 * still outranks both).
 *
 * Everything in this module is pure and derived from the inputs; nothing is
 * persisted. Callers compute these values at read time on every request.
 */

import { WasteManifestSource } from "encamp-prisma/generated/browser";

import { safeParseInt } from "../utils/safeParseNumber";

export { WasteManifestSource };

/**
 * Numeric comparator for transporter rows by `transporterLineNumber` (a string
 * column). Sorts numerically so line "10" doesn't land before "2"; a
 * non-numeric value sorts as 0. Shared by the resolver merge and the two oak
 * renderers so the tie/NaN behavior can't drift across copies.
 */
export function compareByTransporterLineNumber(
  a: { transporterLineNumber: string },
  b: { transporterLineNumber: string }
): number {
  return (
    (safeParseInt(a.transporterLineNumber) ?? 0) - (safeParseInt(b.transporterLineNumber) ?? 0)
  );
}

/**
 * Inputs needed to pick available + primary sources for either a
 * `ProcessedManifest` (uses `vendorManifest.manuallyEdited`) or a
 * `WasteLineUnified` (uses its own `manuallyEdited` column, which means
 * the vendor data was manually edited).
 */
export interface ManifestSourceInputs {
  hasEManifest: boolean;
  hasVendor: boolean;
  /**
   * True only when a document pillar exists AND its extracted data has been
   * human-reviewed ({@link isManifestDocumentReviewed}). An unreviewed
   * document must be passed as `false` — it is not a source.
   */
  hasDocument: boolean;
  /** True when vendor data has been manually edited by a staff user. */
  vendorManuallyEdited?: boolean | null;
}

/**
 * Display priority order. First entry wins when multiple sources are present
 * and `vendorManuallyEdited` is false. The document pillar participates only
 * once reviewed (see {@link ManifestSourceInputs.hasDocument}).
 */
export const MANIFEST_SOURCE_DISPLAY_PRIORITY = [
  WasteManifestSource.ManifestDocument,
  WasteManifestSource.eManifest,
  WasteManifestSource.Vendor,
] as const;

/**
 * Returns which of the three pillars currently have data, in display priority
 * order (ManifestDocument, eManifest, Vendor).
 */
export function getAvailableManifestSources({
  hasEManifest,
  hasVendor,
  hasDocument,
}: ManifestSourceInputs): WasteManifestSource[] {
  const sources: WasteManifestSource[] = [];
  if (hasDocument) sources.push(WasteManifestSource.ManifestDocument);
  if (hasEManifest) sources.push(WasteManifestSource.eManifest);
  if (hasVendor) sources.push(WasteManifestSource.Vendor);
  return sources;
}

/**
 * Pick the primary (display) source according to the rule
 * `document -> eManifest -> vendor`. A (reviewed) document outranks
 * everything — it's the signed paper of record. Below that,
 * `vendorManuallyEdited` keeps vendor ahead of e-Manifest when both exist
 * (preserves intentional staff overrides).
 *
 * Returns `null` only when no source is present (a degenerate state but
 * possible briefly during ingest).
 */
export function getPrimaryManifestSource(inputs: ManifestSourceInputs): WasteManifestSource | null {
  const { hasEManifest, hasVendor, hasDocument, vendorManuallyEdited } = inputs;

  if (hasDocument) return WasteManifestSource.ManifestDocument;

  // Tie-breaker: staff manually edited vendor and we have both pillars, vendor wins.
  if (hasVendor && vendorManuallyEdited === true) {
    return WasteManifestSource.Vendor;
  }

  if (hasEManifest) return WasteManifestSource.eManifest;
  if (hasVendor) return WasteManifestSource.Vendor;
  return null;
}

/**
 * Review state of the source Document behind a `ManifestDocument`-sourced
 * WasteManifest. AI-extracted document data is only treated as a real,
 * surfaceable source once a human has reviewed it.
 */
export interface ManifestDocumentReviewState {
  needsReview?: boolean | null;
  reviewedAt?: Date | string | null;
}

/**
 * Whether a manifest document's parsed data has been human-reviewed and may
 * therefore be surfaced (as a comparable source, in discrepancies, etc.).
 *
 * Fail-closed: a missing DocumentMetadata row counts as NOT reviewed. This is
 * the canonical predicate behind the SQL gate used in the process-manifests
 * pipeline (`COALESCE("needsReview" = false AND "reviewedAt" IS NOT NULL, false)`
 * in createWasteStreams). Keep the two definitions in lockstep.
 */
export function isManifestDocumentReviewed(
  meta: ManifestDocumentReviewState | null | undefined
): boolean {
  return meta != null && meta.needsReview === false && meta.reviewedAt != null;
}

/**
 * Per-source values for one logical field on a manifest line or header.
 * `null` / `undefined` means "this source did not provide a value".
 */
export interface ManifestSourceFieldValues<T> {
  eManifest?: T | null;
  vendor?: T | null;
  document?: T | null;
}

/**
 * Pick the value + source for a single field, given the manifest's
 * primary source.
 *
 * Behavior:
 * - If the primary source has a non-null value, use it.
 * - Otherwise fall back through the display priority order (`document ->
 *   eManifest -> vendor`), skipping the primary we already tried.
 * - When no source has a value, returns `{ value: null, source: null }`.
 *
 * This intentionally does NOT mix sources within a single field (we never
 * report a vendor value labeled as e-Manifest).
 */
export function pickManifestFieldFromPrimary<T>(
  primary: WasteManifestSource | null,
  values: ManifestSourceFieldValues<T>
): { value: T | null; source: WasteManifestSource | null } {
  // Try the primary first.
  if (primary !== null) {
    const primaryValue = readSourceValue(primary, values);
    if (isPresent(primaryValue)) {
      return { value: primaryValue, source: primary };
    }
  }

  // Fall back through the canonical priority order, skipping primary.
  for (const candidate of MANIFEST_SOURCE_DISPLAY_PRIORITY) {
    if (candidate === primary) continue;
    const value = readSourceValue(candidate, values);
    if (isPresent(value)) {
      return { value, source: candidate };
    }
  }

  return { value: null, source: null };
}

function readSourceValue<T>(
  source: WasteManifestSource,
  values: ManifestSourceFieldValues<T>
): T | null | undefined {
  switch (source) {
    case WasteManifestSource.eManifest:
      return values.eManifest;
    case WasteManifestSource.Vendor:
      return values.vendor;
    case WasteManifestSource.ManifestDocument:
      return values.document;
  }
}

function isPresent<T>(value: T | null | undefined): value is T {
  if (value === null || value === undefined) return false;
  // Treat empty strings as absent so a blank source field falls through to a
  // populated alternate source (matches the existing `||` coalesce behavior
  // in `mapWasteLineUnifiedToOutput`).
  if (typeof value === "string" && value.length === 0) return false;
  // Empty arrays count as absent so `federalWasteCodes: []` doesn't shadow a
  // populated array on another source.
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/**
 * Select the waste codes that drive classification for a single line/field.
 *
 * Unlike display (which picks exactly one pillar via
 * {@link getPrimaryManifestSource}), classification takes the UNION of every
 * available source so a code can never be hidden: vendor + eManifest always
 * contribute, and a reviewed document contributes on top. The document is
 * additive — it can introduce codes but never remove vendor/eManifest codes —
 * so classification never downgrades a waste's hazard when a reviewed document
 * reports fewer codes. An UNREVIEWED document contributes nothing (AI
 * extractions stay out of classification until a human signs off); callers
 * pass `documentReviewed` from `WasteLineUnified.documentReviewed` /
 * {@link isManifestDocumentReviewed}.
 *
 * The classification code set is therefore a superset of the single pillar
 * shown in the UI. This is the deliberate, safe direction: nothing that
 * affects the classification is missing from view, though the displayed pillar
 * alone may not list every code the union classified on.
 *
 * Pure and order-independent (dedupes; classification reads membership, not
 * position). State-code normalization, if any, is the caller's responsibility
 * — pass already-normalized arrays when stream hashing needs a canonical form.
 */
export function selectClassificationCodes(
  vendor: readonly string[] | null | undefined,
  eManifest: readonly string[] | null | undefined,
  document: readonly string[] | null | undefined,
  documentReviewed: boolean
): string[] {
  const union = new Set<string>();
  for (const c of vendor ?? []) union.add(c);
  for (const c of eManifest ?? []) union.add(c);
  if (documentReviewed) {
    for (const c of document ?? []) union.add(c);
  }
  return Array.from(union);
}
